import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';

import Box from 'shopper/components/Box';

import Layout from 'components/Layout';
import StoresTabNav from 'components/Store/StoresTabNav';

import { toStoreCouponsLink, toStoreOffersLink } from 'lib/links';
import { stripQs } from 'lib/request';

import Heading from './Heading';
import StoresSidebar from './Sidebar';
import StoresList from './StoresList';

import STORES from 'constants/store';

const INITIAL_TAB_NAME_ACTIVE = STORES.TABS.COUPONS.tabName;
const KEY_TO_ORGANIZE_NUMBERS = '0-9';
const GA_EVENT_CATEGORY = 'stores_page';

const StoresPage = ({ featuredOffers, stores }) => {
  const router = useRouter();
  const [activeTabName, setActiveTabName] = useState(INITIAL_TAB_NAME_ACTIVE);

  const storesSeparatedByAlphabeticalOrder = useMemo(() => {
    const result = { [KEY_TO_ORGANIZE_NUMBERS]: [] };

    stores.forEach((store) => {
      const firstLetter = store.pageTitle[0];

      if (Number(firstLetter)) {
        result[KEY_TO_ORGANIZE_NUMBERS].push(store);
        return;
      }

      const firstLetterWithoutAccentuation = firstLetter
        .toUpperCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

      const letterAlreadyAdded = Object.keys(result).some(
        (letter) => letter === firstLetterWithoutAccentuation
      );

      if (letterAlreadyAdded) {
        Object.entries(result).forEach((keyValue) => {
          if (firstLetterWithoutAccentuation === keyValue[0]) {
            keyValue[1].push(store);
          }
        });
        return;
      }

      result[firstLetterWithoutAccentuation] = [store];
    });

    return result;
  }, []);

  const onTabClickCallback = ({ url, tabName }) => {
    if (url === stripQs(router.asPath)) {
      setActiveTabName(tabName);
    }
  };

  return (
    <Layout
      gaEventCategory={GA_EVENT_CATEGORY}
      headingComponent={<Heading />}
      sidebarComponent={<StoresSidebar featuredOffers={featuredOffers} />}
      maxWidthOnMobileDevices
      scrollToTopButton
    >
      <Box className="px-4 py-10 md:px-20 md:py-16">
        <StoresTabNav
          activeTabName={activeTabName}
          onTabClickCallback={onTabClickCallback}
        />
        <StoresList
          storeUrlFormatterFn={
            activeTabName === STORES.TABS.COUPONS.tabName
              ? toStoreCouponsLink
              : toStoreOffersLink
          }
          storesSeparatedByAlphabeticalOrder={
            storesSeparatedByAlphabeticalOrder
          }
        />
      </Box>
    </Layout>
  );
};

StoresPage.propTypes = {
  featuredOffers: PropTypes.arrayOf(
    PropTypes.shape({
      offerId: PropTypes.number.isRequired,
      offerTitle: PropTypes.string.isRequired,
      offerPrice: PropTypes.number.isRequired,
      offerOldPrice: PropTypes.number,
      offerPriceType: PropTypes.string.isRequired,
      offerPhoto: PropTypes.string.isRequired,
      offerSlug: PropTypes.string.isRequired,
    })
  ).isRequired,
  stores: PropTypes.arrayOf(
    PropTypes.shape({
      pageTitle: PropTypes.string.isRequired,
      pageFrom: PropTypes.string.isRequired,
      pageImage: PropTypes.string.isRequired,
      pageParam: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default StoresPage;
